import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, BellIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import ImportantNewsCard from '../components/ImportantNewsCard';
import ImportantNewsModal from '../components/ImportantNewsModal';
import ConfirmDialog from '../components/ConfirmDialog';

const ImportantNewsPage = () => {
    const [activeTab, setActiveTab] = useState('list');
    const [messages, setMessages] = useState([]);
    const [settings, setSettings] = useState({
        is_section_visible: true,
        marquee_speed: 50,
        background_color: 'red-100',
        text_color: 'red-600'
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMessage, setEditingMessage] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, messageId: null });

    // Fetch messages
    const fetchMessages = async () => {
        try {
            const response = await axios.get('/api/admin/important-news');
            setMessages(response.data.data || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Failed to load messages');
        }
    };

    // Fetch settings
    const fetchSettings = async () => {
        try {
            const response = await axios.get('/api/admin/important-news/settings');
            if (response.data.data) {
                setSettings(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchMessages(), fetchSettings()]);
        setLoading(false);
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const handleAddNew = () => {
        setEditingMessage(null);
        setIsModalOpen(true);
    };

    const handleEdit = (message) => {
        setEditingMessage(message);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, messageId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/important-news/${deleteConfirm.messageId}`);
            toast.success('Message deleted successfully');
            fetchMessages();
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Failed to delete message');
        }
    };


    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingMessage(null);
    };

    const handleSave = () => {
        handleModalClose();
        fetchMessages();
    };

    const handleSettingsChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/important-news/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Important News</h1>
                    <p className="text-slate-500 mt-1">Manage security alerts and important announcements</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-1.5 inline-flex gap-1">
                <button
                    onClick={() => setActiveTab('list')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'list'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <BellIcon className="h-5 w-5" />
                    Messages ({messages.length})
                </button>
                <button
                    onClick={() => setActiveTab('settings')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'settings'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <Cog6ToothIcon className="h-5 w-5" />
                    Settings
                </button>
            </div>

            {/* List Tab */}
            {activeTab === 'list' && (
                <div className="space-y-6">
                    <div className="flex justify-end">
                        <button
                            onClick={handleAddNew}
                            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <PlusIcon className="h-5 w-5" />
                            Add Message
                        </button>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 gap-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white rounded-2xl h-24 animate-pulse border border-slate-100"></div>
                            ))}
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
                            <PlusIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-slate-800">No messages yet</h3>
                            <p className="text-slate-500 mb-6">Start by adding your first important news message</p>
                            <button
                                onClick={handleAddNew}
                                className="bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold"
                            >
                                Add First Message
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            {messages.map((msg) => (
                                <ImportantNewsCard
                                    key={msg.id}
                                    message={msg}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-6">Section Settings</h2>
                    <div className="space-y-6 max-w-2xl">
                        <div>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={settings.is_section_visible}
                                    onChange={(e) => handleSettingsChange('is_section_visible', e.target.checked)}
                                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                                />
                                <div>
                                    <span className="block text-sm font-semibold text-slate-700">Show Section</span>
                                    <span className="block text-xs text-slate-500">Display important news section on homepage</span>
                                </div>
                            </label>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Marquee Speed (seconds)
                            </label>
                            <input
                                type="number"
                                min="10"
                                max="100"
                                value={settings.marquee_speed}
                                onChange={(e) => handleSettingsChange('marquee_speed', parseInt(e.target.value))}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                            <p className="text-xs text-slate-500 mt-1">Lower values = faster scrolling</p>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Background Color (Tailwind class)
                            </label>
                            <input
                                type="text"
                                value={settings.background_color}
                                onChange={(e) => handleSettingsChange('background_color', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                placeholder="red-100"
                            />
                            <p className="text-xs text-slate-500 mt-1">Example: red-100, yellow-50, blue-100</p>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Text Color (Tailwind class)
                            </label>
                            <input
                                type="text"
                                value={settings.text_color}
                                onChange={(e) => handleSettingsChange('text_color', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                placeholder="red-600"
                            />
                            <p className="text-xs text-slate-500 mt-1">Example: red-600, yellow-700, blue-800</p>
                        </div>
                        <div className="pt-4">
                            <button
                                onClick={handleSettingsSave}
                                disabled={savingSettings}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50"
                            >
                                {savingSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <ImportantNewsModal
                    message={editingMessage}
                    onClose={handleModalClose}
                    onSave={handleSave}
                />
            )}

            {/* Delete Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, messageId: null })}
                onConfirm={confirmDelete}
                title="Delete Message"
                message="Are you sure you want to delete this message? This action cannot be undone."
            />
        </div>
    );
};

export default ImportantNewsPage;
